import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
    applyMasterRouteBlockTimeRefresh,
    applyRouteBlockTimeStaPolicy,
    applyStaFromCurrentBlockTime,
    applyStdChangeAbandoningManual,
    clearStaManual,
    describeStaLeak,
    findStaLeaks,
    formatUtcClock,
    formatUtcDateClock,
    isManualStaStillValid,
    isStaLeak,
    markStaManual,
    sanitizeStaManualForSave,
} from "../../../../utils/staBlockTimeAlignment";
import { convertFromUTC } from "../../../../utils/timezoneUtils";
import { getTimezoneByMode } from "../../../../contexts/TimeZoneContext";

dayjs.extend(utc);
dayjs.extend(timezone);

/** 提示里最多列几班明细，超出只报总数 */
const MAX_NOTICE_ITEMS = 8;

/**
 * 旧版编辑器：到达跟着当前轮档走。
 * 纯规则集中在 staBlockTimeAlignment；这里只做列表应用和给运控看的说明。
 */
function applyByIds(flights, ids, mapper) {
    const idSet = new Set(ids);
    return flights.map((flight) => (idSet.has(flight.id) ? mapper(flight) : flight));
}

/**
 * 到达变化区间：与表格同一套时区口径（LOCAL 模式取到达机场时区）。
 * 同一显示日只写时刻，跨一天写“次日”，跨更多天写完整日期。
 */
function formatStaChangeRange(change, timeZone, airportsMap) {
    const targetTimezone = getTimezoneByMode(timeZone, change.arrivalAirport, airportsMap);
    const from = convertFromUTC(change.fromSta, targetTimezone);
    const to = convertFromUTC(change.toSta, targetTimezone);
    if (!from || !from.isValid() || !to || !to.isValid()) {
        return `${formatUtcDateClock(change.fromSta)} → ${formatUtcDateClock(change.toSta)}`;
    }
    const dayDiff = to.startOf("day").diff(from.startOf("day"), "day");
    if (dayDiff === 0) {
        return `${from.format("HH:mm")} → ${to.format("HH:mm")}`;
    }
    if (dayDiff === 1) {
        return `${from.format("MM-DD HH:mm")} → 次日 ${to.format("HH:mm")}`;
    }
    return `${from.format("MM-DD HH:mm")} → ${to.format("MM-DD HH:mm")}`;
}

function formatStaChangeLine(change, timeZone, airportsMap) {
    const number = change.flightNumber || "未命名航班";
    const leg = `${change.departureAirport || "?"}→${change.arrivalAirport || "?"}`;
    return `${number} ${leg} ${formatStaChangeRange(change, timeZone, airportsMap)}`;
}

function formatChangePreview(changes, timeZone, airportsMap) {
    const preview = changes.slice(0, MAX_NOTICE_ITEMS).map((change) => formatStaChangeLine(change, timeZone, airportsMap)).join("；");
    const extra = changes.length > MAX_NOTICE_ITEMS ? ` 等共 ${changes.length} 班` : "";
    return `${preview}${extra}`;
}

const StaFollowBlockTimeService = {
    findLeaks(flights) {
        return findStaLeaks(flights).map(describeStaLeak);
    },

    describeLeak: describeStaLeak,

    applyAlignedSta(flights, ids) {
        return applyByIds(flights, ids, (flight) => applyStaFromCurrentBlockTime(clearStaManual(flight)));
    },

    applyKeepManualSta(flights, ids) {
        return applyByIds(flights, ids, (flight) => markStaManual(flight));
    },

    applyAlignedStaToAllLeaks(flights) {
        const leakIds = findStaLeaks(flights).map((flight) => flight.id);
        return this.applyAlignedSta(flights, leakIds);
    },

    applyRouteBlockTimeStaPolicy,

    applyMasterRouteBlockTimeRefresh,

    applyStdChangeAbandoningManual,

    shouldKeepManualStaOnRouteChange(flight, newBlockMinutes) {
        return isManualStaStillValid(flight, newBlockMinutes);
    },

    collectStaRewrites(beforeFlights, afterFlights) {
        const beforeMap = new Map((beforeFlights || []).map((flight) => [flight.id, flight]));
        return (afterFlights || [])
            .filter((flight) => {
                const before = beforeMap.get(flight.id);
                return before && before.sta !== flight.sta;
            })
            .map((flight) => ({
                id: flight.id,
                flightNumber: flight.flight_number,
                departureAirport: flight.departure_airport,
                arrivalAirport: flight.arrival_airport,
                fromSta: beforeMap.get(flight.id).sta,
                toSta: flight.sta,
            }));
    },

    /**
     * 只有到达变化时的话术（例如已绑航路的轮档在主数据里更新）。
     * 时间跟随页面时区口径（LOCAL 模式取到达机场时区），与表格列一致。
     */
    formatStaRewriteNotice(changes, { timeZone = "UTC", airportsMap = {} } = {}) {
        if (!changes || changes.length === 0) return "";
        return `已按航路最新轮档调整到达（起飞不变）：${formatChangePreview(changes, timeZone, airportsMap)}`;
    },

    /**
     * 刷新完成后的到达播报：有改绑有顺延就一条说清因果；只有改绑只报数量；
     * 没改绑但有顺延（例如已绑航路的轮档更新）仍报到达到变化。
     */
    formatRefreshRebindNotice(reboundCount, changes, { timeZone = "UTC", airportsMap = {} } = {}) {
        const rebound = Number(reboundCount) || 0;
        const list = changes || [];
        if (rebound > 0 && list.length === 0) {
            return `已按默认航路改绑 ${rebound} 班`;
        }
        if (rebound <= 0) {
            return this.formatStaRewriteNotice(list, { timeZone, airportsMap });
        }
        return `已按默认航路改绑 ${rebound} 班；其中 ${list.length} 班到达时间顺延（起飞不变）：${formatChangePreview(list, timeZone, airportsMap)}`;
    },

    prepareFlightsForSave(flights) {
        if (!Array.isArray(flights)) return flights;
        return flights.map((flight) => sanitizeStaManualForSave(flight));
    },

    formatClock: formatUtcClock,
    formatDateClock: formatUtcDateClock,
    isStaLeak,
};

export default StaFollowBlockTimeService;
